import React, { useState } from 'react';
import { Box, Typography, Paper, Divider, TextField, Stack, Chip } from '@mui/material';
import CopyButton from './CopyButton';
import ShareButton from './ShareButton';
import QrButton from './QrButton';
import FormFieldsPane from './FormFieldsPane';
import { t } from '../i18n';
import {
  formatPostal,
  japaneseBase,
  japaneseFull,
  englishFull,
  japaneseAssembled,
  englishAssembled,
  ecFormFields,
  jaFormFields,
  PLACEHOLDER_JA,
  PLACEHOLDER_EN,
} from '../utils/address';

const dashedBox = {
  p: 2,
  mt: 1,
  bgcolor: 'background.paper',
  borderRadius: 1,
  border: '1px dashed',
  borderColor: 'divider',
};

const placeholderSx = { fontStyle: 'italic', color: 'primary.main' };

// Join the two supplement inputs into one string for the assembled line.
const joinSupp = (banchi, building) =>
  [banchi.trim(), building.trim()].filter(Boolean).join(' ');

// Entry type badge — business addresses are complete (they carry the
// establishment name + 番地/building), so no supplement field is offered for them.
function entryKind(entry) {
  if (entry.business) return { label: t('Business', '事業所'), color: 'secondary' };
  if (entry.is_building) return { label: t('Building', 'ビル'), color: 'primary' };
  return { label: t('Address', '住所'), color: 'default' };
}

// One result entry on the Postal Code page. Shows the base Japanese/English
// address (copyable). Business (大口事業所) entries lead with a dedicated
// bilingual company-name field. Ordinary entries get optional banchi + building
// inputs that feed both the assembled line and a collapsible "fill an online
// form" field-by-field breakdown (JA form under 日本語, EC form under English).
const EntryCard = ({ entry, postalCode, index, total }) => {
  const [jaBanchi, setJaBanchi] = useState('');
  const [jaBuilding, setJaBuilding] = useState('');
  const [enBanchi, setEnBanchi] = useState('');
  const [enBuilding, setEnBuilding] = useState('');

  const shareUrl = `${window.location.origin}/?zip=${postalCode}`;
  const business = entry.business;
  const complete = !!business; // business address needs no supplement
  const kind = entryKind(entry);
  const firstOpen = index === 0; // expand the top entry's form pane, collapse the rest

  const jaSupp = joinSupp(jaBanchi, jaBuilding);
  const enSupp = joinSupp(enBanchi, enBuilding);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        bgcolor: 'background.default',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="overline" color="text.secondary">
          {t('Entry', 'エントリ')} {index + 1} / {total}
        </Typography>
        <Chip size="small" label={kind.label} color={kind.color} variant="outlined" />
      </Box>

      {/* ---- Company name (business entries only) ---- */}
      {business && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            bgcolor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'secondary.dark',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="overline" color="secondary">
              {t('Company name', '会社名')}
            </Typography>
            <CopyButton text={business.kanji} addressType="company_kanji" />
          </Box>
          {/* Japanese on top (authoritative), English below. */}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.35 }}>
            {business.kanji}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {business.romaji}
          </Typography>
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
            {t(
              'The English company name is machine-romanized and may be inaccurate — keep the Japanese name above when addressing mail.',
              '英語の会社名は自動変換のため正確でない場合があります。郵送の際は上記の日本語表記をそのままご利用ください。'
            )}
          </Typography>
        </Box>
      )}

      {/* ---- Japanese ---- */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {t('Japanese', '日本語')}
        </Typography>
        <CopyButton text={japaneseFull(entry, postalCode)} addressType="japanese_base" />
      </Box>
      <Typography variant="body1" sx={{ fontWeight: 600, color: 'common.white' }}>
        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, mr: 1 }}>
          〒{formatPostal(postalCode)}
        </Box>
        {japaneseBase(entry)}
      </Typography>

      {!complete && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              color="secondary"
              value={jaBanchi}
              onChange={(e) => setJaBanchi(e.target.value)}
              label={t('番地 (street no., optional)', '番地（任意）')}
            />
            <TextField
              fullWidth
              size="small"
              color="secondary"
              value={jaBuilding}
              onChange={(e) => setJaBuilding(e.target.value)}
              label={t('建物名・部屋番号 (optional)', '建物名・部屋番号（任意）')}
            />
          </Stack>
          <Box sx={dashedBox}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'common.white' }}>
                {japaneseFull(entry, postalCode)}{' '}
                <Box component="span" sx={jaSupp ? null : placeholderSx}>
                  {jaSupp || PLACEHOLDER_JA}
                </Box>
              </Typography>
              <CopyButton
                text={japaneseAssembled(entry, postalCode, jaSupp)}
                addressType="japanese_assembled"
              />
            </Box>
          </Box>
          <FormFieldsPane
            title={t('Fill a Japanese form', 'フォームに入力する')}
            rows={jaFormFields(entry, postalCode, jaBanchi, jaBuilding)}
            defaultOpen={firstOpen}
            addressType="ja_form"
          />
        </>
      )}

      <Divider sx={{ my: 2 }} />

      {/* ---- English ---- */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {t('English', '英語')}
        </Typography>
        <CopyButton text={englishFull(entry, postalCode)} addressType="english_base" />
      </Box>
      <Typography variant="body1" sx={{ fontWeight: 600, color: 'common.white' }}>
        {englishFull(entry, postalCode)}
      </Typography>

      {!complete && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              color="secondary"
              value={enBanchi}
              onChange={(e) => setEnBanchi(e.target.value)}
              label={t('Street no. (optional)', 'Street no.（任意）')}
            />
            <TextField
              fullWidth
              size="small"
              color="secondary"
              value={enBuilding}
              onChange={(e) => setEnBuilding(e.target.value)}
              label={t('Building / room (optional)', '建物名・部屋（英・任意）')}
            />
          </Stack>
          <Box sx={dashedBox}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'common.white' }}>
                <Box component="span" sx={enSupp ? null : placeholderSx}>
                  {enSupp || PLACEHOLDER_EN}
                </Box>{' '}
                {englishFull(entry, postalCode)}
              </Typography>
              <CopyButton
                text={englishAssembled(entry, postalCode, enSupp)}
                addressType="english_assembled"
              />
            </Box>
          </Box>
          <FormFieldsPane
            title={t('Fill an online form (EC / checkout)', 'オンラインフォームに入力する（EC）')}
            rows={ecFormFields(entry, postalCode, enBanchi, enBuilding)}
            defaultOpen={firstOpen}
            addressType="ec_form"
          />
        </>
      )}

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={1}>
        <ShareButton url={shareUrl} title={japaneseFull(entry, postalCode)} />
        <QrButton url={shareUrl} label={`〒${formatPostal(postalCode)}`} />
      </Stack>
    </Paper>
  );
};

export default EntryCard;
